import { App, Button, Card, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Membership } from '@goelectric/shared-types';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWriteMemberships } from '@/layouts/navigation';
import { membershipApi } from '../api/membershipApi';
import { MembershipTable } from '../components/MembershipTable';
import { useMemberships } from '../hooks/useMemberships';
import { membershipErrorMessage } from '../utils/errors';
import { canFilterMembershipStatus } from '../utils/roles';

/**
 * Listado de Membresías (US-2.4, `/membresias`) — paginación server-side vía
 * `useMemberships`, con búsqueda por nombre (debounce) y filtro por estado. Los
 * roles de plataforma ven `ALL`; ALIADO_ADMIN/CONTADOR quedan fijos en `ACTIVE`
 * porque el backend les restringe el estado y el detalle de las inactivas. La
 * escritura es solo `SUPER_ADMIN`.
 */
export function MembershipsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message } = App.useApp();
  const canFilterStatus = canFilterMembershipStatus(user?.role);
  const memberships = useMemberships({
    statusScope: canFilterStatus ? 'all' : 'active-only',
  });

  const canWrite = canWriteMemberships(user?.role);
  const goToCreate = () => navigate('/membresias/nueva');

  const remove = async (membership: Membership) => {
    try {
      await membershipApi.remove(membership.id);
      message.success('Membresía desactivada.');
      // Si se dio de baja el último elemento de una página > 0, retrocede una página
      // para no dejar un empty-state engañoso.
      if (memberships.data.length === 1 && memberships.offset > 0) {
        memberships.setOffset(Math.max(0, memberships.offset - memberships.limit));
      } else {
        memberships.refresh();
      }
    } catch (caught) {
      message.error(
        membershipErrorMessage(caught, 'No se pudo eliminar la membresía.'),
      );
    }
  };

  return (
    <div className="membresias-page">
      <div className="membresias-page__header">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Membresías
          </Typography.Title>
          <Typography.Text type="secondary">
            Gestiona las membresías mensuales y anuales con tarifa preferencial.
          </Typography.Text>
        </div>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={goToCreate}>
            Nueva membresía
          </Button>
        )}
      </div>

      <Card>
        <MembershipTable
          memberships={memberships.data}
          meta={memberships.meta}
          loading={memberships.loading}
          error={memberships.error}
          filters={memberships.filters}
          canWrite={canWrite}
          canFilterStatus={canFilterStatus}
          onFiltersChange={memberships.setFilters}
          onOffsetChange={memberships.setOffset}
          onRetry={memberships.refresh}
          onCreate={goToCreate}
          onSelect={(membership) =>
            navigate(`/membresias/${encodeURIComponent(membership.id)}`)
          }
          onEdit={(membership) =>
            navigate(`/membresias/${encodeURIComponent(membership.id)}/editar`)
          }
          onDelete={(membership) => void remove(membership)}
        />
      </Card>
    </div>
  );
}
